import React from "react";
import { Outlet, Link } from "react-router-dom";
import { Landmark, ShieldCheck } from "lucide-react";
import { TID } from "@/constants/testIds";

/**
 * Marketing shell (header + footer) — pensé conversion :
 * - Logo agrandi (~40 % plus grand qu'auparavant) pour ancrer la marque en < 2s.
 * - Sous-titre « Patrimoine SaaS » discret, italique, doré.
 * - Lien Connexion volontairement discret (ghost text).
 * - CTA principal « Créer mon compte » toujours accessible en haut à droite.
 * - Footer nettoyé de tout vocabulaire technique.
 */
export default function MarketingLayout() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="container-app flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="marketing-logo">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary shadow-[0_2px_8px_rgba(17,47,36,0.12)] transition-transform group-hover:scale-105">
              <Landmark className="h-6 w-6 text-accent" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-[26px] text-foreground tracking-tight">Fonbex</div>
              <div className="text-[10px] italic tracking-[0.15em] text-accent/80 mt-0.5">
                le pilote de votre patrimoine
              </div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#benefits" className="link-underline hover:text-foreground">Pourquoi Fonbex</a>
            <a href="#pricing" className="link-underline hover:text-foreground">Formules</a>
            <a href="#demo" className="link-underline hover:text-foreground">Démonstration</a>
          </nav>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link
              to="/login"
              data-testid={TID.landingLogin}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-full"
            >
              Connexion
            </Link>
            <Link
              to="/register"
              data-testid={TID.landingCta}
              className="rounded-full bg-primary px-4 sm:px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors shadow-[0_2px_8px_rgba(17,47,36,0.15)]"
            >
              Créer mon compte
            </Link>
          </div>
        </div>
      </header>
      <main><Outlet /></main>
      <footer className="border-t border-border/60 bg-secondary/30 mt-16">
        <div className="container-app py-14 grid gap-10 md:grid-cols-4 text-sm">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
                <Landmark className="h-5 w-5 text-accent" />
              </div>
              <div>
                <div className="font-serif text-xl text-foreground">Fonbex</div>
                <div className="text-[9px] italic tracking-[0.15em] text-accent/80">le pilote de votre patrimoine</div>
              </div>
            </Link>
            <p className="mt-4 text-muted-foreground max-w-xs leading-relaxed">
              La plateforme française qui centralise la gestion de votre patrimoine
              immobilier, locatif et fiscal.
            </p>
            <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" />
              Hébergement en France · Conforme RGPD
            </div>
          </div>
          <div>
            <div className="text-overline mb-3">Produit</div>
            <ul className="space-y-1.5 text-muted-foreground">
              <li>Gestion locative</li>
              <li>Comptabilité SCI</li>
              <li>Génération de baux</li>
              <li>Suivi des crédits</li>
              <li>Coffre-fort documents</li>
            </ul>
          </div>
          <div>
            <div className="text-overline mb-3">Ressources</div>
            <ul className="space-y-1.5 text-muted-foreground">
              <li>Guides fiscaux</li>
              <li>Centre d&apos;aide</li>
              <li>Blog patrimonial</li>
              <li>Contact</li>
            </ul>
          </div>
          <div>
            <div className="text-overline mb-3">Confiance</div>
            <ul className="space-y-1.5 text-muted-foreground">
              <li>CGU</li>
              <li>Politique de confidentialité</li>
              <li>RGPD</li>
              <li>Sécurité</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border/60">
          <div className="container-app py-4 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
            <span>© 2026 Fonbex · Fait en France 🇫🇷</span>
            <span>contact@fonbex.fr</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
